import { defineColumns } from './piColumns.js';
import { EMPTY, formatDateFull, formatInteger, formatPercent, formatPeriod, isNumber } from './piFormat.js';
import { indicatorValue } from './piOverviewModel.js';
import { MAX_PAGE_SIZE, buildApiQuery, nextSort, toQueryString } from './piQuery.js';
import { companyLink } from './piNavigation.js';
import { listOf, objectOf } from './piRequests.js';
import { countText, metricCell, pctMetric } from './piTable.js';
import { normalizeMetric, showsValue } from './piState.js';

export const ADVANCED_REPORT_FEATURE = 102;

export const USAGE_FIELDS = ['period', 'roleClass', 'sizeBand', 'segment'];

export const USAGE_PAGE_SIZE = 25;

export const USER_SORTS = ['lastVisit', 'visits', 'configs', 'results'];

export const DEFAULT_USAGE_LIST = { stage: '', sort: 'lastVisit', desc: true, page: 1, pageSize: USAGE_PAGE_SIZE, base: '' };

export const SMALL_SAMPLE_VISITS = 30;

export const USAGE_SEGMENTS = [
  { key: 'opened_only', label: 'Só abriu', group: 'confusion' },
  { key: 'value', label: 'Tira valor', group: 'value' },
  { key: 'oneoff', label: 'Uso pontual', group: 'value' },
  { key: 'stuck', label: 'Travou', group: 'confusion' },
  { key: 'explored', label: 'Explorou sem resultado', group: 'confusion' },
  { key: 'viewed', label: 'Consultou na tela', group: 'neutral' },
];

export const USAGE_GROUPS = {
  value: { label: 'Valor', tone: 'ok' },
  confusion: { label: 'Confusão', tone: 'bad' },
  neutral: { label: 'Neutro', tone: 'muted' },
};

export const FUNNEL_LABELS = {
  opened: 'Abriu',
  ran: 'Executou',
  changed: 'Mudou a configuração',
  result: 'Teve resultado',
  returned: 'Voltou em até 7 dias',
};

export const MODE_LABELS = { list: 'Lista', group: 'Agrupado' };

export const KIND_LABELS = {
  open: 'Abrir',
  run: 'Executar',
  export: 'Exportar',
  save: 'Salvar',
  delete: 'Excluir',
  favorite: 'Favoritar',
  other: 'Outra ação',
};

const cellMetric = (field) => (row) => {
  const cell = objectOf(row)[field];
  return cell && cell.metric ? cell.metric : null;
};

const field = (name) => (row) => objectOf(row)[name];

export const USER_COLUMNS = defineColumns([
  {
    key: 'company', label: 'Empresa', type: 'text', infoKey: 'common.company', width: 40,
    value: (row) => String(objectOf(row).companyName || objectOf(row).companyId || '').trim(),
  },
  { key: 'companyId', label: 'Identificador da empresa', type: 'text', infoKey: 'common.companyId', screen: false },
  { key: 'shardCode', label: 'Banco', type: 'text', infoKey: 'common.shard', screen: false },
  {
    key: 'user', label: 'Usuário', type: 'text', infoKey: 'features.report.user', description: 'Id encurtado (últimos 4 caracteres), sem nome de pessoa.',
    value: (row) => shortUserId(objectOf(row).userId),
  },
  { key: 'role', label: 'Papel', type: 'text', infoKey: 'features.report.userRole', value: field('roleLabel') },
  {
    key: 'segment', label: 'Segmento', type: 'text', infoKey: 'features.report.userSegment',
    value: (row) => String(objectOf(row).segmentLabel || '').trim() || (segmentInfo(objectOf(row).segment) ? segmentInfo(objectOf(row).segment).label : ''),
  },
  { key: 'visits', label: 'Visitas', type: 'int', infoKey: 'features.report.userVisits', sortable: true, startDescending: true },
  { key: 'attempts', label: 'Execuções', type: 'int', infoKey: 'features.report.userAttempts', description: 'Execuções da 1ª página, com qualquer resposta.' },
  {
    key: 'goodDays', label: 'Dias com resultado', type: 'int', infoKey: 'features.report.userGoodDays',
    description: 'Dias com pelo menos uma execução na tela que respondeu e não veio vazia.',
  },
  {
    key: 'configs', label: 'Configurações', type: 'int', infoKey: 'features.report.userConfigs', sortable: true, startDescending: true,
    description: 'Configurações diferentes tentadas; embaixo, quantas foram exportadas ou salvas.', value: field('configsTried'),
  },
  { key: 'resultConfigs', label: 'Configurações com resultado', type: 'int', infoKey: 'features.report.userResultConfigs', screen: false },
  {
    key: 'results', label: 'Resultados', type: 'int', infoKey: 'features.report.userResults', sortable: true, startDescending: true,
    description: 'Exportações e relatórios salvos.',
  },
  {
    key: 'badShare', label: 'Execuções ruins', type: 'percent', infoKey: 'features.report.userBadShare',
    description: 'Execuções vazias, recusadas ou com erro técnico ÷ execuções.', value: (row) => (isNumber(objectOf(row).badShare) ? objectOf(row).badShare * 100 : null),
  },
  { key: 'lastVisit', label: 'Última visita', type: 'date', infoKey: 'features.report.userLastVisit', sortable: true, startDescending: true, value: field('lastVisitOn') },
  { key: 'topFailure', label: 'Falha mais comum', type: 'text', infoKey: 'features.report.userTopFailure' },
]);

export const USAGE_HINT = 'Visitas à tela Relatórios avançados da V2 (abrir, executar, exportar, salvar). Só admin, admin secundário e contador têm acesso; os filtros de período, papel, porte e perfil valem aqui.';

export const USAGE_DISABLED_MESSAGE = 'O uso do relatório avançado ainda não está disponível neste ambiente (404): a Read API ainda não tem esta consulta.';

export const NOT_MEASURED_TEXT = 'Os eventos do relatório avançado ainda não foram ligados.';

export const FUNNEL_NOTE = 'Executou é fração de Abriu; Mudou a configuração, Teve resultado e Voltou são frações de Executou e não se encaixam uma na outra (exportar o modelo sem mexer é resultado). Voltou só conta visitas com os 7 dias seguintes já fechados.';

export const CHANGE_NOTE = 'Uma configuração = a mesma estrutura (relatório, modo, campos de filtro e de agrupamento, métricas e período); trocar só o valor de um filtro não muda a configuração.';

export const DEPTH_NOTE = 'Percentis sobre as visitas que executaram.';

export const COMBINATION_NOTE = 'Combinação = relatório × modo × campos de agrupamento; colunas, filtros e período não mudam a combinação. Só entram as 20 primeiras executadas por 3 usuários ou mais; o resto soma em outras combinações.';

export const EMPTY_RUNS_NOTE = 'Execuções da 1ª página que responderam sem nenhuma linha; só relatórios com 5 execuções ou mais.';

export const USERS_NOTE = 'Admins e contadores das empresas clientes, sem nome: o id aparece encurtado. Ordenar por contagem ordena quem usa a tela, não é ranking de colaborador.';

const HIDDEN_NOTE_PREFIXES = ['Dados até ', 'Coletando: ', 'Funil, combinações e onde trava usam'];

const COLLECTING_DAYS = /(\d+) de (\d+) dias/;

const MATURITY_STATES = ['notMeasured', 'collecting', 'ready'];

const textOf = (value) => (value === null || value === undefined ? '' : String(value).trim());

const numberOrNull = (value) => (isNumber(value) ? value : null);

const plural = (value, one, many) => formatInteger(value) + ' ' + (value === 1 ? one : many);

const capitalized = (value) => (value ? value.charAt(0).toUpperCase() + value.substring(1) : '');

const sentence = (value) => {
  const clean = capitalized(textOf(value));
  if (!clean) return '';
  return /[.!?]$/.test(clean) ? clean : clean + '.';
};

const positiveInteger = (value, fallback, max) => {
  const number = Math.floor(Number(value));
  if (!isFinite(number) || number < 1) return fallback;
  return max && number > max ? max : number;
};

export const isAdvancedReport = (featureType) => Number(featureType) === ADVANCED_REPORT_FEATURE;

export const segmentInfo = (key) => USAGE_SEGMENTS.filter((segment) => segment.key === key)[0] || null;

export const groupInfo = (group) => (Object.prototype.hasOwnProperty.call(USAGE_GROUPS, group) ? USAGE_GROUPS[group] : USAGE_GROUPS.neutral);

export const validStage = (value) => (segmentInfo(textOf(value)) ? textOf(value) : '');

export const validSort = (value) => (USER_SORTS.indexOf(textOf(value)) !== -1 ? textOf(value) : DEFAULT_USAGE_LIST.sort);

export const usageList = (list) => {
  const source = objectOf(list);
  return {
    stage: validStage(source.stage),
    sort: validSort(source.sort),
    desc: source.desc === undefined ? DEFAULT_USAGE_LIST.desc : source.desc === true,
    page: positiveInteger(source.page, 1),
    pageSize: positiveInteger(source.pageSize, USAGE_PAGE_SIZE, MAX_PAGE_SIZE),
    base: textOf(source.base),
  };
};

export const usageRequest = (filters, list, now) => {
  const base = buildApiQuery(filters, { fields: USAGE_FIELDS, now: now });
  const baseKey = toQueryString(base);
  const state = usageList(list);
  const page = state.base === baseKey ? state.page : 1;
  const params = buildApiQuery(filters, {
    fields: USAGE_FIELDS,
    now: now,
    extra: { stage: state.stage, sort: state.sort, desc: state.desc, page: page, pageSize: state.pageSize },
  });
  return { baseKey: baseKey, params: params, key: toQueryString(params), page: page };
};

export const stagedList = (list, stage) => {
  const state = usageList(list);
  const wanted = validStage(stage);
  return Object.assign({}, state, { stage: state.stage === wanted ? '' : wanted, page: 1 });
};

export const sortedList = (list, column) => {
  const state = usageList(list);
  const source = objectOf(column);
  const next = nextSort(state, textOf(source.sort) || textOf(source.key), source.startDescending);
  return Object.assign({}, state, { sort: validSort(next.sort), desc: next.desc, page: 1 });
};

export const pagedList = (list, page, baseKey) =>
  Object.assign({}, usageList(list), { page: positiveInteger(page, 1), base: textOf(baseKey) });

export const resizedList = (list, pageSize) =>
  Object.assign({}, usageList(list), { pageSize: positiveInteger(pageSize, USAGE_PAGE_SIZE, MAX_PAGE_SIZE), page: 1 });

export const maturityView = (data) => {
  const source = objectOf(data);
  const maturity = objectOf(source.maturity);
  const state = MATURITY_STATES.indexOf(maturity.state) !== -1 ? maturity.state : (source.measuredFrom ? 'ready' : 'notMeasured');
  const reason = textOf(maturity.reason);
  const readyOn = formatDateFull(maturity.readyOn);
  const measuredFrom = formatDateFull(source.measuredFrom);
  const since = measuredFrom !== EMPTY ? 'Medido desde ' + measuredFrom : '';
  if (state === 'notMeasured') {
    return { state: state, ready: false, measured: false, badgeText: 'Sem medição', tone: 'muted', text: sentence(reason) || NOT_MEASURED_TEXT, detail: '' };
  }
  if (state === 'collecting') {
    const days = COLLECTING_DAYS.exec(reason);
    const until = readyOn !== EMPTY ? ' A janela fica completa em ' + readyOn + '.' : '';
    return {
      state: state,
      ready: false,
      measured: true,
      badgeText: days ? 'Coletando: ' + days[1] + ' de ' + days[2] + ' dias' : 'Coletando',
      tone: 'warn',
      text: (days
        ? 'Segmentos, empresas e usuários ainda com a janela de ' + days[2] + ' dias incompleta: os números são parciais.'
        : sentence(reason) || 'Coletando dados do relatório avançado.') + until,
      detail: since,
    };
  }
  return { state: 'ready', ready: true, measured: true, badgeText: since || 'Medido', tone: 'ok', text: '', detail: '' };
};

export const headerView = (data, requestedTo) => {
  const source = objectOf(data);
  const range = objectOf(source.window);
  const population = objectOf(source.population);
  const asOf = formatDateFull(source.asOf);
  const companies = numberOrNull(population.companies);
  const users = numberOrNull(population.users);
  const visits = numberOrNull(population.visits);
  const parts = [];
  if (companies !== null) parts.push(plural(companies, 'empresa', 'empresas'));
  if (users !== null) parts.push(plural(users, 'usuário', 'usuários'));
  if (visits !== null) parts.push(plural(visits, 'visita', 'visitas'));
  const period = formatPeriod(range.from, range.to);
  const segmentPeriod = formatPeriod(range.segmentFrom, range.segmentTo);
  const segmentDays = numberOrNull(range.segmentDays);
  const gap = numberOrNull(range.visitGapMinutes);
  const returnDays = numberOrNull(range.returnDays);
  const to = textOf(range.to);
  const wanted = textOf(requestedTo);
  const periodEmpty = !companies && !users && !visits;
  return {
    asOfText: asOf !== EMPTY ? 'Dados até ' + asOf : '',
    populationText: parts.length ? 'No período: ' + parts.join(' · ') : '',
    windowText: [
      period !== EMPTY ? 'Funil, combinações e onde trava: ' + period : '',
      segmentPeriod !== EMPTY ? 'segmentos, empresas e usuários: ' + segmentPeriod + (segmentDays !== null ? ' (' + formatInteger(segmentDays) + ' dias)' : '') : '',
    ].filter(Boolean).join(' · '),
    visitText: gap !== null
      ? 'Visita = eventos do mesmo usuário com menos de ' + formatInteger(gap) + ' min entre si' +
        (returnDays !== null ? '; voltou = executou de novo em outro dia, em até ' + formatInteger(returnDays) + ' dias' : '') + '.'
      : '',
    cutText: to && wanted && wanted > to ? 'O fim do período foi cortado em ' + formatDateFull(to) + ', último dia montado.' : '',
    periodEmpty: periodEmpty,
    periodEmptyText: periodEmpty
      ? 'Ninguém entrou no relatório avançado no período com estes filtros. Segmentos, empresas e usuários abaixo usam a janela ' +
        (segmentPeriod !== EMPTY ? 'de ' + segmentPeriod : 'própria') + '.'
      : '',
    population: { companies: companies, users: users, visits: visits },
  };
};

const percentilesText = (value) => {
  const source = objectOf(value);
  const parts = [];
  if (isNumber(source.p75)) parts.push('p75 ' + formatInteger(source.p75));
  if (isNumber(source.p90)) parts.push('p90 ' + formatInteger(source.p90));
  return parts.join(' · ');
};

const groupShareText = (segments, group) => {
  const users = listOf(segments).filter((segment) => textOf(segment.group) === group);
  return users.map((segment) => textOf(segment.label)).filter(Boolean).join(' + ');
};

export const headlineCards = (data) => {
  const source = objectOf(data);
  const headline = objectOf(source.headline);
  const depth = objectOf(objectOf(source.depth).configsPerVisit);
  const p50 = numberOrNull(headline.configsPerVisitP50);
  const neutral = normalizeMetric(headline.neutral);
  const valueParts = groupShareText(source.segments, 'value');
  const confusionParts = groupShareText(source.segments, 'confusion');
  return [
    {
      key: 'value',
      label: 'Tiram valor',
      metric: headline.value || null,
      definitionKey: 'report_user_segment',
      infoKey: 'features.report.value',
      lines: [(valueParts || 'Tira valor + Uso pontual') + ', nos 30 dias'],
    },
    {
      key: 'confused',
      label: 'Se confundem ou desistem',
      metric: headline.confused || null,
      definitionKey: 'report_user_segment',
      infoKey: 'features.report.confused',
      lines: [(confusionParts || 'Travou + Explorou sem resultado + Só abriu') + ', nos 30 dias']
        .concat(showsValue(neutral) ? ['Consultaram só na tela (neutro): ' + formatPercent(neutral.value)] : []),
    },
    {
      key: 'resultRate',
      label: 'Taxa de resultado por visita',
      metric: headline.resultRate || null,
      definitionKey: 'report_visit_funnel',
      infoKey: 'features.report.resultRate',
      lines: ['Visitas que executaram e exportaram ou salvaram'],
    },
    {
      key: 'configsPerVisit',
      label: 'Configurações por visita (mediana)',
      metric: {
        value: p50,
        unit: 'count',
        state: p50 === null ? 'na' : 'ok',
        stateReason: p50 === null ? 'nenhuma visita executou no período' : null,
        definitionKey: 'report_visit_funnel',
      },
      definitionKey: 'report_visit_funnel',
      infoKey: 'features.report.configsPerVisit',
      lines: [percentilesText(depth), 'Configurações diferentes tentadas nas visitas que executaram'].filter(Boolean),
    },
  ];
};

const BASE_PHRASES = { opened: 'das que abriram', ran: 'das que executaram' };

const stepLabel = (key, label) => textOf(label) || FUNNEL_LABELS[key] || key || 'Etapa';

export const funnelRows = (data) => {
  const steps = listOf(objectOf(data).funnel);
  const labels = {};
  steps.forEach((step) => {
    labels[textOf(step.key)] = stepLabel(textOf(step.key), step.label);
  });
  return steps.map((step, index) => {
    const key = textOf(step.key) || String(index);
    const visits = numberOrNull(step.visits);
    const base = textOf(step.base);
    const eligible = numberOrNull(step.eligible);
    const rate = step.rate && typeof step.rate === 'object' ? normalizeMetric(step.rate) : null;
    const basePhrase = BASE_PHRASES[base] || (base ? 'da etapa ' + (labels[base] || stepLabel(base, null)) : '');
    const open = !!rate && (rate.state === 'immature' || eligible === 0);
    let rateText = '';
    let title = basePhrase ? 'Fração ' + basePhrase.replace(/^das que /, 'das visitas que ') : 'Todas as visitas do período';
    if (eligible !== null) {
      title = eligible === 0
        ? 'Nenhuma visita que executou já tem os 7 dias seguintes fechados'
        : 'Sobre ' + plural(eligible, 'visita que executou', 'visitas que executaram') + ' com os 7 dias seguintes fechados';
    }
    if (open) rateText = 'janela aberta';
    else if (rate && showsValue(rate)) {
      rateText = formatPercent(rate.value) + (eligible !== null
        ? ' de ' + plural(eligible, 'elegível', 'elegíveis')
        : (basePhrase ? ' ' + basePhrase : ''));
    } else if (rate) rateText = 'n/d';
    return {
      key: key,
      label: stepLabel(key, step.label),
      value: visits,
      visits: visits,
      rate: rate,
      eligible: eligible,
      baseText: basePhrase,
      valueText: countText(visits) + (rateText ? ' · ' + rateText : ''),
      title: title,
    };
  });
};

const branchRow = (key, label, hint, branch) => {
  const source = objectOf(branch);
  const visits = numberOrNull(source.visits);
  return {
    key: key,
    label: label,
    hint: hint,
    visits: visits,
    visitsText: countText(visits),
    resultRate: metricCell(source.resultRate),
    returnRate: metricCell(source.returnRate),
  };
};

export const changeRows = (data) => {
  const effect = objectOf(objectOf(data).changeEffect);
  return [
    branchRow('unchanged', 'Rodou uma configuração só', 'Executou sem mudar a estrutura', effect.unchanged),
    branchRow('changed', 'Mudou a configuração', '2 ou mais configurações tentadas na visita', effect.changed),
  ];
};

export const changeSummary = (rows) => {
  const list = listOf(rows);
  const unchanged = list.filter((row) => row.key === 'unchanged')[0];
  const changed = list.filter((row) => row.key === 'changed')[0];
  if (!unchanged || !changed || !unchanged.resultRate.visible || !changed.resultRate.visible) return '';
  const left = unchanged.resultRate.metric.value;
  const right = changed.resultRate.metric.value;
  const text = 'Quem mudou a configuração teve resultado em ' + formatPercent(right) + ' das visitas; quem rodou uma só, em ' + formatPercent(left) + '.';
  const small = (unchanged.visits !== null && unchanged.visits < SMALL_SAMPLE_VISITS) || (changed.visits !== null && changed.visits < SMALL_SAMPLE_VISITS);
  return small ? text + ' Amostra pequena (menos de ' + SMALL_SAMPLE_VISITS + ' visitas de um dos lados): a diferença não é conclusiva.' : text;
};

const DEPTH_ROWS = [
  { key: 'configsPerVisit', label: 'Configurações tentadas' },
  { key: 'runsPerVisit', label: 'Execuções' },
  { key: 'minutesPerVisit', label: 'Minutos na visita' },
];

export const depthRows = (data) => {
  const depth = objectOf(objectOf(data).depth);
  return DEPTH_ROWS.map((row) => {
    const source = objectOf(depth[row.key]);
    return {
      key: row.key,
      label: row.label,
      p50: numberOrNull(source.p50),
      p75: numberOrNull(source.p75),
      p90: numberOrNull(source.p90),
      p50Text: countText(source.p50),
      p75Text: countText(source.p75),
      p90Text: countText(source.p90),
    };
  });
};

export const segmentRows = (data, stage) => listOf(objectOf(data).segments).map((segment, index) => {
  const key = textOf(segment.key) || String(index);
  const known = segmentInfo(key);
  const group = textOf(segment.group) || (known ? known.group : 'neutral');
  const info = groupInfo(group);
  const users = numberOrNull(segment.users);
  return {
    key: key,
    label: textOf(segment.label) || (known ? known.label : key),
    definition: textOf(segment.definition),
    group: group,
    groupLabel: info.label,
    tone: info.tone,
    users: users,
    usersText: countText(users),
    companies: numberOrNull(segment.companies),
    companiesText: countText(segment.companies),
    share: metricCell(segment.share, { ratio: false }),
    selectable: !!known,
    selected: !!stage && stage === key,
  };
});

const modeText = (mode) => MODE_LABELS[textOf(mode)] || textOf(mode) || EMPTY;

const groupKeyText = (key) => {
  const source = objectOf(key);
  const label = textOf(source.label) || textOf(source.binding) || 'Campo';
  return label + (textOf(source.bucket) === 'month' ? ' (por mês)' : '');
};

export const groupByText = (mode, keys) => {
  const list = listOf(keys).map(groupKeyText);
  if (list.length) return list.join(' › ');
  return textOf(mode) === 'list' ? 'Sem agrupamento' : EMPTY;
};

const recordText = (record, label) => textOf(label) || textOf(record) || EMPTY;

export const combinationRows = (data) => listOf(objectOf(data).combinations).map((combination, index) => {
  const bindings = listOf(combination.groupBy).map((key) => textOf(key.binding) + (key.bucket ? ':' + textOf(key.bucket) : '')).join(',');
  return {
    key: [textOf(combination.record), textOf(combination.mode), bindings, String(index)].join('|'),
    recordText: recordText(combination.record, combination.recordLabel),
    modeText: modeText(combination.mode),
    groupByText: groupByText(combination.mode, combination.groupBy),
    users: numberOrNull(combination.usersRan),
    companies: numberOrNull(combination.companies),
    visitsRan: numberOrNull(combination.visitsRan),
    visitsResult: numberOrNull(combination.visitsResult),
    usersReturned: numberOrNull(combination.usersReturned),
    usersText: countText(combination.usersRan),
    companiesText: isNumber(combination.companies) ? plural(combination.companies, 'empresa', 'empresas') : '',
    visitsRanText: countText(combination.visitsRan),
    visitsResultText: countText(combination.visitsResult),
    resultRate: metricCell(combination.resultRate),
    usersReturnedText: countText(combination.usersReturned),
    returnRate: metricCell(combination.returnRate),
  };
});

export const otherCombinationRow = (data) => {
  const source = objectOf(objectOf(data).otherCombinations);
  const combinations = numberOrNull(source.combinations);
  if (combinations === null || combinations < 1) return null;
  return {
    key: 'other',
    label: combinations === 1 ? '1 outra combinação' : formatInteger(combinations) + ' outras combinações',
    hint: 'Fora do ranking: menos de 3 usuários ou além das 20 primeiras',
    users: numberOrNull(source.usersRan),
    visitsRan: numberOrNull(source.visitsRan),
    visitsResult: numberOrNull(source.visitsResult),
    usersText: countText(source.usersRan),
    visitsRanText: countText(source.visitsRan),
    visitsResultText: countText(source.visitsResult),
    resultRate: metricCell(pctMetric(numberOrNull(source.visitsResult), numberOrNull(source.visitsRan))),
  };
};

export const isTechnicalCode = (code) => {
  const value = textOf(code);
  return !!value && value.indexOf('REPORT.') !== 0 && value !== 'BusinessException';
};

export const failureRows = (data) => listOf(objectOf(objectOf(data).problems).failures).map((failure, index) => {
  const code = textOf(failure.code);
  const kind = textOf(failure.kind);
  return {
    key: [kind, code, textOf(failure.record), String(index)].join('|'),
    label: textOf(failure.label) || code || 'Falha',
    code: code,
    technical: isTechnicalCode(code),
    kindText: KIND_LABELS[kind] || kind || EMPTY,
    recordText: textOf(failure.record) ? recordText(failure.record, failure.recordLabel) : 'Qualquer relatório',
    events: numberOrNull(failure.events),
    users: numberOrNull(failure.users),
    companies: numberOrNull(failure.companies),
    ended: kind === 'run' ? numberOrNull(failure.endedVisits) : null,
    eventsText: countText(failure.events),
    usersText: countText(failure.users),
    companiesText: countText(failure.companies),
    endedText: kind === 'run' ? countText(failure.endedVisits) : EMPTY,
  };
});

export const emptyRows = (data) => listOf(objectOf(objectOf(data).problems).empty).map((row, index) => ({
  key: [textOf(row.record), textOf(row.mode), String(index)].join('|'),
  recordText: recordText(row.record, row.recordLabel),
  modeText: modeText(row.mode),
  runs: numberOrNull(row.runs),
  emptyRuns: numberOrNull(row.emptyRuns),
  users: numberOrNull(row.users),
  runsText: countText(row.runs),
  emptyRunsText: countText(row.emptyRuns),
  emptyRate: metricCell(row.emptyRate),
  usersText: countText(row.users),
}));

export const technicalView = (data) => {
  const source = objectOf(objectOf(objectOf(data).problems).technical);
  const events = numberOrNull(source.events) || 0;
  const users = numberOrNull(source.users) || 0;
  const types = listOf(source.types).map((item, index) => ({
    key: textOf(item.type) || String(index),
    type: textOf(item.type) || 'Erro',
    eventsText: countText(item.events),
  }));
  return {
    events: events,
    text: events > 0
      ? plural(events, 'erro técnico', 'erros técnicos') + ' de ' + plural(users, 'usuário', 'usuários') + ' no período, à parte das recusas: ' +
        types.map((item) => item.type + ' (' + item.eventsText + ')').join(', ') + '.'
      : 'Nenhum erro técnico no período.',
    types: types,
  };
};

export const companyRows = (data, query) => listOf(objectOf(data).companies).map((company, index) => ({
  key: textOf(company.shardCode) + ':' + (textOf(company.companyId) || String(index)),
  name: textOf(company.name) || textOf(company.companyId) || 'Empresa',
  companyId: textOf(company.companyId),
  shardCode: textOf(company.shardCode),
  link: companyLink(textOf(company.companyId), textOf(company.shardCode), query),
  users: numberOrNull(company.users),
  valueUsers: numberOrNull(company.valueUsers),
  confusedUsers: numberOrNull(company.confusedUsers),
  visits: numberOrNull(company.visits),
  results: numberOrNull(company.results),
  usersText: countText(company.users),
  valueUsersText: countText(company.valueUsers),
  confusedUsersText: countText(company.confusedUsers),
  visitsText: countText(company.visits),
  resultsText: countText(company.results),
}));

export const shortUserId = (userId) => {
  const clean = textOf(userId).replace(/[^A-Za-z0-9-]/g, '');
  return clean ? 'Usuário …' + clean.substring(Math.max(0, clean.length - 4)).toUpperCase() : 'Usuário';
};

export const userRows = (data, query) => listOf(objectOf(objectOf(data).users).rows).map((row, index) => {
  const segment = textOf(row.segment);
  const known = segmentInfo(segment);
  const info = groupInfo(known ? known.group : 'neutral');
  const tried = numberOrNull(row.configsTried);
  const resultConfigs = numberOrNull(row.resultConfigs);
  const badShare = numberOrNull(row.badShare);
  return {
    key: textOf(row.shardCode) + ':' + textOf(row.companyId) + ':' + String(index) + ':' + shortUserId(row.userId),
    companyName: textOf(row.companyName) || textOf(row.companyId) || 'Empresa',
    companyId: textOf(row.companyId),
    shardCode: textOf(row.shardCode),
    link: companyLink(textOf(row.companyId), textOf(row.shardCode), query),
    userText: shortUserId(row.userId),
    roleText: textOf(row.roleLabel) || EMPTY,
    segment: segment,
    segmentText: textOf(row.segmentLabel) || (known ? known.label : segment) || EMPTY,
    tone: info.tone,
    visitsText: countText(row.visits),
    attemptsText: countText(row.attempts),
    goodDaysText: countText(row.goodDays),
    configsText: countText(tried),
    resultConfigsText: resultConfigs !== null && resultConfigs > 0 ? formatInteger(resultConfigs) + ' com resultado' : '',
    resultsText: countText(row.results),
    badShareText: badShare === null ? EMPTY : formatPercent(badShare * 100, 0),
    lastVisitText: formatDateFull(row.lastVisitOn),
    topFailureText: textOf(row.topFailure) || EMPTY,
  };
});

export const usersPaging = (data, list) => {
  const users = objectOf(objectOf(data).users);
  const state = usageList(list);
  return {
    total: isNumber(users.total) && users.total >= 0 ? Math.floor(users.total) : listOf(users.rows).length,
    page: isNumber(users.page) && users.page >= 1 ? Math.floor(users.page) : state.page,
    pageSize: isNumber(users.pageSize) && users.pageSize >= 1 ? Math.floor(users.pageSize) : state.pageSize,
  };
};

export const stageText = (stage, segments) => {
  const key = validStage(stage);
  if (!key) return '';
  const listed = listOf(segments).filter((segment) => segment.key === key)[0];
  const label = listed ? listed.label : segmentInfo(key).label;
  return 'Só o segmento ' + label + (listed && listed.users !== null ? ' (' + plural(listed.users, 'usuário', 'usuários') + ')' : '');
};

export const usageView = (data, options) => {
  const source = objectOf(data);
  const settings = objectOf(options);
  const list = usageList(settings.list);
  const segments = segmentRows(source, list.stage);
  const change = changeRows(source);
  const range = objectOf(source.window);
  return {
    featureType: numberOrNull(source.featureType),
    window: { from: textOf(range.from), to: textOf(range.to) },
    neutral: objectOf(source.headline).neutral || null,
    query: objectOf(settings.query),
    header: headerView(source, settings.requestedTo),
    maturity: maturityView(source),
    cards: headlineCards(source),
    funnel: funnelRows(source),
    funnelNote: FUNNEL_NOTE,
    change: change,
    changeSummary: changeSummary(change),
    changeNote: CHANGE_NOTE,
    depth: depthRows(source),
    depthNote: DEPTH_NOTE,
    segments: segments,
    combinations: combinationRows(source),
    otherCombination: otherCombinationRow(source),
    combinationNote: COMBINATION_NOTE,
    failures: failureRows(source),
    emptyRuns: emptyRows(source),
    emptyRunsNote: EMPTY_RUNS_NOTE,
    technical: technicalView(source),
    companies: companyRows(source, settings.query),
    users: userRows(source, settings.query),
    usersNote: USERS_NOTE,
    paging: usersPaging(source, list),
    stage: list.stage,
    stageText: stageText(list.stage, segments),
    limitations: listOf(source.limitations).map(textOf).filter(Boolean),
  };
};

export const usageEmpty = (view) => {
  if (!view || !view.maturity.measured || !view.header.periodEmpty) return false;
  const segmentUsers = view.segments.reduce((sum, segment) => sum + (segment.users || 0), 0);
  return !segmentUsers && !view.companies.length && !view.paging.total;
};

export const usageEmptyMessage = (data) => (formatDateFull(objectOf(data).asOf) === EMPTY
  ? 'O uso do relatório avançado ainda não foi montado.'
  : 'Ninguém entrou no relatório avançado no período com estes filtros.');

export const usageEnvelope = (envelope) => {
  if (!envelope || typeof envelope !== 'object') return envelope;
  const notes = listOf(envelope.notes).filter((note) => !HIDDEN_NOTE_PREFIXES.some((prefix) => textOf(note).indexOf(prefix) === 0));
  return Object.assign({}, envelope, { notes: notes });
};

export const usageError = (error) => {
  if (!error || error.kind !== 'disabled') return error;
  return Object.assign({}, error, { message: USAGE_DISABLED_MESSAGE });
};

export const REPORT_KPI_COLUMNS = defineColumns([
  { key: 'period', label: 'Período', type: 'text', infoKey: 'filter.period', screen: false },
  { key: 'value', label: 'Tiram valor', type: 'metric', unit: 'pct', infoKey: 'features.report.value', value: indicatorValue('value') },
  { key: 'confused', label: 'Se confundem ou desistem', type: 'metric', unit: 'pct', infoKey: 'features.report.confused', value: indicatorValue('confused') },
  { key: 'neutral', label: 'Consultaram só na tela (neutro)', type: 'metric', unit: 'pct', infoKey: 'features.report.neutral', value: indicatorValue('neutral') },
  { key: 'resultRate', label: 'Taxa de resultado por visita', type: 'metric', unit: 'pct', infoKey: 'features.report.resultRate', value: indicatorValue('resultRate') },
  {
    key: 'configsPerVisit', label: 'Configurações por visita (mediana)', type: 'metric', unit: 'count', infoKey: 'features.report.configsPerVisit',
    value: indicatorValue('configsPerVisit'),
  },
]);

export const reportIndicatorItems = (view) => listOf(objectOf(view).cards)
  .map((card) => ({ key: card.key, metric: card.metric }))
  .concat([{ key: 'neutral', metric: objectOf(view).neutral }]);

export const REPORT_FUNNEL_COLUMNS = defineColumns([
  { key: 'label', label: 'Etapa', type: 'text', infoKey: 'features.report.funnelStep' },
  { key: 'visits', label: 'Visitas', type: 'int', infoKey: 'features.report.funnelVisits' },
  {
    key: 'rate', label: 'Taxa', type: 'metric', unit: 'pct', infoKey: 'features.report.funnelRate',
    value: (row) => objectOf(row).rate || { value: null, unit: 'pct', state: 'ok' },
  },
  { key: 'baseText', label: 'Base da taxa', type: 'text', infoKey: 'features.report.funnelBase', screen: false },
  { key: 'eligible', label: 'Visitas elegíveis', type: 'int', infoKey: 'features.report.funnelEligible', screen: false },
]);

export const REPORT_CHANGE_COLUMNS = defineColumns([
  { key: 'label', label: 'Na visita', type: 'text', infoKey: 'features.report.changeBranch' },
  { key: 'hint', label: 'Critério', type: 'text', infoKey: 'features.report.changeBranch', screen: false },
  { key: 'visits', label: 'Visitas', type: 'int', infoKey: 'features.report.changeVisits' },
  { key: 'resultRate', label: 'Resultado', type: 'metric', unit: 'pct', infoKey: 'features.report.changeResult', value: cellMetric('resultRate') },
  {
    key: 'returnRate', label: 'Voltou', type: 'metric', unit: 'pct', infoKey: 'features.report.changeReturn', value: cellMetric('returnRate'),
    description: 'Executou de novo em outro dia, em até 7 dias',
  },
]);

export const REPORT_DEPTH_COLUMNS = defineColumns([
  { key: 'label', label: 'Por visita', type: 'text', infoKey: 'features.report.depthMeasure' },
  { key: 'p50', label: 'Mediana', type: 'int', infoKey: 'features.report.depthP50' },
  { key: 'p75', label: 'p75', type: 'int', infoKey: 'features.report.depthP75' },
  { key: 'p90', label: 'p90', type: 'int', infoKey: 'features.report.depthP90' },
]);

export const REPORT_SEGMENT_COLUMNS = defineColumns([
  { key: 'label', label: 'Segmento', type: 'text', infoKey: 'features.report.segment' },
  { key: 'definition', label: 'Definição', type: 'text', infoKey: 'features.report.segmentDefinition', screen: false, width: 60 },
  { key: 'groupLabel', label: 'Grupo', type: 'text', infoKey: 'features.report.segmentGroup' },
  { key: 'users', label: 'Usuários', type: 'int', infoKey: 'features.report.segmentUsers' },
  { key: 'companies', label: 'Empresas', type: 'int', infoKey: 'features.report.segmentCompanies' },
  { key: 'share', label: 'Participação', type: 'metric', unit: 'pct', infoKey: 'features.report.segmentShare', value: cellMetric('share') },
]);

export const REPORT_COMBINATION_COLUMNS = defineColumns([
  { key: 'recordText', label: 'Relatório', type: 'text', infoKey: 'features.report.record' },
  { key: 'companies', label: 'Empresas', type: 'int', infoKey: 'features.report.comboCompanies', screen: false },
  { key: 'modeText', label: 'Modo', type: 'text', infoKey: 'features.report.mode' },
  { key: 'groupByText', label: 'Agrupado por', type: 'text', infoKey: 'features.report.groupBy' },
  { key: 'users', label: 'Usuários', type: 'int', infoKey: 'features.report.comboUsers' },
  { key: 'visitsRan', label: 'Visitas que executaram', type: 'int', infoKey: 'features.report.comboVisitsRan' },
  { key: 'visitsResult', label: 'Com resultado', type: 'int', infoKey: 'features.report.comboVisitsResult' },
  { key: 'resultRate', label: 'Taxa de resultado', type: 'metric', unit: 'pct', infoKey: 'features.report.comboResultRate', value: cellMetric('resultRate') },
  { key: 'returnRate', label: 'Voltaram a executar', type: 'metric', unit: 'pct', infoKey: 'features.report.comboReturn', value: cellMetric('returnRate') },
  { key: 'usersReturned', label: 'Usuários que voltaram', type: 'int', infoKey: 'features.report.comboUsersReturned', screen: false },
]);

export const combinationExportRows = (view) => {
  const source = objectOf(view);
  const rows = listOf(source.combinations);
  const other = source.otherCombination;
  if (!other) return rows;
  return rows.concat([{
    key: other.key,
    recordText: other.label,
    companies: null,
    modeText: '',
    groupByText: other.hint,
    users: other.users,
    visitsRan: other.visitsRan,
    visitsResult: other.visitsResult,
    resultRate: other.resultRate,
    returnRate: null,
    usersReturned: null,
  }]);
};

export const REPORT_FAILURE_COLUMNS = defineColumns([
  { key: 'label', label: 'Falha', type: 'text', infoKey: 'features.report.failure', width: 40 },
  { key: 'code', label: 'Código', type: 'text', infoKey: 'features.report.failureCode', screen: false },
  { key: 'technical', label: 'Erro técnico', type: 'text', infoKey: 'features.report.failureTechnical', screen: false },
  { key: 'kindText', label: 'Ação', type: 'text', infoKey: 'features.report.failureKind' },
  { key: 'recordText', label: 'Relatório', type: 'text', infoKey: 'features.report.record' },
  { key: 'events', label: 'Ocorrências', type: 'int', infoKey: 'features.report.failureEvents' },
  { key: 'users', label: 'Usuários', type: 'int', infoKey: 'features.report.failureUsers' },
  { key: 'companies', label: 'Empresas', type: 'int', infoKey: 'features.report.failureCompanies' },
  {
    key: 'ended', label: 'Visitas que terminaram no erro', type: 'int', infoKey: 'features.report.failureEnded',
    description: 'Visitas cuja última execução foi esta falha, sem uma execução bem-sucedida depois',
  },
]);

export const REPORT_EMPTY_COLUMNS = defineColumns([
  { key: 'recordText', label: 'Relatório', type: 'text', infoKey: 'features.report.record' },
  { key: 'modeText', label: 'Modo', type: 'text', infoKey: 'features.report.mode' },
  { key: 'runs', label: 'Execuções', type: 'int', infoKey: 'features.report.emptyRuns' },
  { key: 'emptyRuns', label: 'Vazias', type: 'int', infoKey: 'features.report.emptyCount' },
  { key: 'emptyRate', label: 'Taxa de vazias', type: 'metric', unit: 'pct', infoKey: 'features.report.emptyRate', value: cellMetric('emptyRate') },
  { key: 'users', label: 'Usuários', type: 'int', infoKey: 'features.report.emptyUsers' },
]);

export const REPORT_COMPANY_COLUMNS = defineColumns([
  { key: 'name', label: 'Empresa', type: 'text', infoKey: 'common.company', width: 40 },
  { key: 'companyId', label: 'Identificador da empresa', type: 'text', infoKey: 'common.companyId', screen: false },
  { key: 'shardCode', label: 'Banco', type: 'text', infoKey: 'common.shard', screen: false },
  { key: 'users', label: 'Usuários', type: 'int', infoKey: 'features.report.companyUsers' },
  { key: 'valueUsers', label: 'Tiram valor', type: 'int', infoKey: 'features.report.companyValue' },
  { key: 'confusedUsers', label: 'Se confundem', type: 'int', infoKey: 'features.report.companyConfused' },
  { key: 'visits', label: 'Visitas', type: 'int', infoKey: 'features.report.companyVisits' },
  { key: 'results', label: 'Resultados', type: 'int', infoKey: 'features.report.companyResults' },
]);

export const usageLines = (view) => {
  const header = objectOf(objectOf(view).header);
  return [header.windowText, header.visitText].map(textOf).filter(Boolean);
};

export const userListLines = (view, sortState) => {
  const source = objectOf(view);
  const state = objectOf(sortState);
  const column = USER_COLUMNS.filter((item) => item.key === state.sort)[0];
  const lines = usageLines(source);
  if (textOf(source.stageText)) lines.push(textOf(source.stageText));
  if (column) lines.push('Ordem: ' + column.label + (state.desc === false ? ', crescente' : ', decrescente'));
  return lines;
};



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piAdvancedReportModel.js